import { Injectable, inject } from '@angular/core';
import { EChartsOption } from 'echarts';
import { ISeriesPoint } from '../models/series-point.model';
import { Theme, ThemeService } from './theme.service';

/**
 * Colores de serie del tablero. Los nombres son los historicos (`yellow`,
 * `orange`, `lime`…) porque las paginas y `data/program-health.data.ts` los
 * usan por nombre; el tono lo pone ahora el token de `styles.scss`.
 */
interface IChartPalette {
  accent: string;
  accent2: string;
  amber: string;
  blue: string;
  blue2: string;
  green: string;
  gridLine: string;
  lime: string;
  magenta: string;
  mint: string;
  muted: string;
  orange: string;
  page: string;
  purple: string;
  red: string;
  surface: string;
  teal: string;
  text: string;
  yellow: string;
}

/**
 * Cromo de la grafica leido de los tokens del tema vigente. Un canvas no hereda
 * variables CSS: hay que resolverlas en JS cada vez que se arma una opcion.
 */
interface IChartChrome {
  axis: string;
  glassBorder: string;
  grid: string;
  elevation: string;
  heading: string;
  mapBorder: string;
  mapEmpty: string;
  mapHover: string;
  muted: string;
  onColor: string;
  onWarm: string;
  symbolEdge: string;
  text: string;
  tooltipBg: string;
  track: string;
}

/**
 * Fabricas de opciones de ECharts del tablero.
 *
 * Dos familias de color conviven aqui, y las dos salen de los tokens:
 *  - las de SERIE (teal, magenta, semaforo NPS…) son de marca y valen lo mismo
 *    en ambos temas: se pasan por parametro o salen de COLORS / STACK_PALETTE;
 *  - las de CROMO (ejes, rejilla, tooltip, etiquetas, pista de la dona) cambian
 *    con el tema, por eso `applyTheme()` las reescribe sobre la opcion ya
 *    armada cuando el usuario alterna claro/oscuro.
 */
@Injectable({ providedIn: 'root' })
export class ChartService {

  private readonly themeService = inject(ThemeService);

  /**
   * Instancia raiz del servicio de tema. `COLORS` y `STACK_PALETTE` son
   * estaticos —las paginas y `data/program-health.data.ts` los leen sin
   * inyectar nada— y desde ahi no se puede llamar a `inject()`.
   */
  private static themeRef?: ThemeService;

  /** Paleta ya resuelta; se descarta al cambiar de tema. */
  private static paletteCache?: { key: string; colors: IChartPalette; stack: Array<string> };

  /** Cromo ya resuelto del tema vigente; se descarta al cambiar de tema. */
  private chromeCache?: { theme: Theme; value: IChartChrome };

  constructor() {
    ChartService.themeRef = this.themeService;
  }

  /* ------------------------------------------------------------------ */
  /*                          ANCHOR paleta                             */
  /* ------------------------------------------------------------------ */

  /**
   * Colores de marca de las series, leidos de los tokens del tema.
   *
   * Es un getter y no un `static readonly`: una constante se evaluaria al
   * cargar el modulo, cuando puede que la hoja de estilos todavia no este
   * aplicada, y congelaria los respaldos para toda la sesion.
   *
   * La paleta nueva —cian/violeta— tiene menos tonos que la del sitio
   * original, asi que varias claves calidas comparten `--z-pasivo` y las
   * verdosas `--z-green`. No es un descuido: son el mismo rol de color.
   *
   * `surface`, `page`, `text`, `muted` y `gridLine` se conservan por
   * compatibilidad de la API; el cromo de la grafica ya no sale de aqui.
   */
  public static get COLORS(): IChartPalette {
    return ChartService.palette().colors;
  }

  /** Paleta de la barra apilada "Drivers de recomendacion". */
  public static get STACK_PALETTE(): Array<string> {
    return ChartService.palette().stack;
  }

  /**
   * Un token inexistente devuelve cadena vacia: el respaldo es el valor del
   * token en tema oscuro, de modo que una lectura anticipada (antes de que
   * exista el servicio de tema) da el mismo color.
   */
  private static token(name: string, fallback: string): string {
    return ChartService.themeRef?.token(name, fallback) ?? fallback;
  }

  private static palette(): { key: string; colors: IChartPalette; stack: Array<string> } {
    // 'boot' = todavia no hay servicio de tema, se esta leyendo el respaldo:
    // se cachea aparte para volver a resolver en cuanto lo haya.
    const key = ChartService.themeRef ? ChartService.themeRef.theme() : 'boot';

    if (ChartService.paletteCache?.key === key) {
      return ChartService.paletteCache;
    }

    const t = (name: string, fallback: string): string => ChartService.token(name, fallback);
    const colors: IChartPalette = {
      /* marca */
      teal:     t('--z-teal',     '#06b6d4'),
      magenta:  t('--z-magenta',  '#ec4899'),
      purple:   t('--z-purple',   '#a855f7'),
      blue:     t('--z-blue',     '#6366f1'),
      blue2:    t('--z-blue-2',   '#38bdf8'),
      lime:     t('--z-green',    '#2dd4a7'),
      mint:     t('--z-green',    '#2dd4a7'),

      /* semaforo NPS */
      red:      t('--z-detractor', '#f43f5e'),
      amber:    t('--z-pasivo',    '#f5a524'),
      green:    t('--z-promotor',  '#2dd4a7'),
      yellow:   t('--z-pasivo',    '#f5a524'),
      orange:   t('--z-pasivo',    '#f5a524'),

      /* acentos: los unicos que cambian con el tema */
      accent:   t('--z-accent',   '#22d3ee'),
      accent2:  t('--z-accent-2', '#a78bfa'),

      /* compatibilidad */
      surface:  t('--z-glass-soft',   'rgba(255, 255, 255, .035)'),
      page:     t('--z-bg-base',      '#070b16'),
      text:     t('--z-text-heading', '#f4f7fc'),
      muted:    t('--z-text-muted',   '#92a3bf'),
      gridLine: t('--z-chart-grid',   'rgba(255, 255, 255, .10)'),
    };

    // Orden pensado para que dos segmentos contiguos de la barra apilada nunca
    // caigan en la misma familia de tono.
    const stack: Array<string> = [
      colors.teal,
      colors.magenta,
      colors.mint,
      colors.purple,
      colors.amber,
      colors.blue,
      colors.red,
      colors.blue2,
    ];

    ChartService.paletteCache = { key, colors, stack };

    return ChartService.paletteCache;
  }

  /* ------------------------------------------------------------------ */
  /*                        ANCHOR cromo del tema                       */
  /* ------------------------------------------------------------------ */

  /**
   * Los tokens siempre existen en styles.scss, por eso no llevan respaldo.
   * El resultado se memoriza por tema: cada lectura fuerza un recalculo de
   * estilos y una pagina llega a armar diez graficas de golpe.
   */
  private chrome(): IChartChrome {
    const theme = this.themeService.theme();

    if (this.chromeCache?.theme === theme) {
      return this.chromeCache.value;
    }

    const token = (name: string): string => this.themeService.token(name);
    const value: IChartChrome = {
      axis:        token('--z-chart-axis'),
      elevation:   token('--z-elev-2'),
      glassBorder: token('--z-glass-border'),
      grid:        token('--z-chart-grid'),
      heading:     token('--z-text-heading'),
      mapBorder:   token('--z-bg-base'),
      mapEmpty:    token('--z-chart-map-empty'),
      mapHover:    token('--z-glass-active'),
      muted:       token('--z-text-muted'),
      onColor:     token('--z-on-color'),
      onWarm:      token('--z-on-warm'),
      // El borde de los simbolos recorta el punto contra el cristal; en blanco
      // fijo se pierde en tema claro.
      symbolEdge:  token('--z-bg-base'),
      text:        token('--z-text'),
      tooltipBg:   token('--z-chart-tooltip-bg'),
      track:       token('--z-chart-track'),
    };

    this.chromeCache = { theme, value };

    return value;
  }

  /**
   * Un blanco puro como color de serie desaparece en el tema claro: se
   * reemplaza por el acento del tema, que si contrasta en ambos.
   */
  private seriesColor(color: string): string {
    const value = color.trim().toLowerCase();
    const isWhite = value === '#fff' || value === '#ffffff' || value === 'white';

    return isWhite ? this.themeService.token('--z-accent') : color;
  }

  /** Canales de un hex de marca; null si el color no es hexadecimal. */
  private channels(color: string): Array<number> | null {
    const hex = color.trim();

    if (!hex.startsWith('#')) {
      return null;
    }

    const full = hex.length === 4
      ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
      : hex;
    const value = parseInt(full.slice(1, 7), 16);

    return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
  }

  /** rgba a partir de un hex de marca: degradados y glows de las series. */
  private withAlpha(color: string, alpha: number): string {
    const rgb = this.channels(color);

    return rgb ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})` : color;
  }

  /** Oscurece un color de marca sin volverlo translucido (barras opacas). */
  private shade(color: string, factor: number): string {
    const rgb = this.channels(color);

    return rgb
      ? `rgb(${rgb.map((v) => Math.round(v * factor)).join(', ')})`
      : color;
  }

  /**
   * Elige el texto que si contrasta sobre un color de marca: blanco solo
   * cuando el fondo es oscuro, si no el token de texto sobre color calido.
   * Umbral de luminancia relativa por debajo del cual gana el blanco (4.5:1).
   */
  private labelOn(color: string): string {
    const c = this.chrome();
    const rgb = this.channels(color);

    if (!rgb) {
      return c.onColor;
    }

    const linear = rgb
      .map((v) => v / 255)
      .map((v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
    const luminance = 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];

    return luminance < 0.2 ? c.onColor : c.onWarm;
  }

  /** Degradado vertical de una serie: lo que hace que se vea "de cristal". */
  private verticalGradient(color: string, from = 1, to = 0.5): any {
    return {
      type: 'linear',
      x: 0,
      y: 0,
      x2: 0,
      y2: 1,
      colorStops: [
        { offset: 0, color: this.withAlpha(color, from) },
        { offset: 1, color: this.withAlpha(color, to) },
      ],
    };
  }

  /**
   * Degradado opaco para barras con etiqueta encima del propio color: si el
   * segmento fuera translucido el texto perderia contraste sobre el cristal.
   */
  private solidGradient(color: string): any {
    return {
      type: 'linear',
      x: 0,
      y: 0,
      x2: 0,
      y2: 1,
      colorStops: [
        { offset: 0, color },
        { offset: 1, color: this.shade(color, 0.82) },
      ],
    };
  }

  /* ------------------------------------------------------------------ */
  /*                        ANCHOR tooltip base                         */
  /* ------------------------------------------------------------------ */

  /** Solo la parte del tooltip que depende del tema (la reusa applyTheme). */
  private tooltipChrome(): any {
    const c = this.chrome();

    return {
      axisPointer: { lineStyle: { color: c.axis, type: 'dashed' } },
      backgroundColor: c.tooltipBg,
      borderColor: c.glassBorder,
      borderRadius: 10,
      borderWidth: 1,
      extraCssText:
        `backdrop-filter: blur(12px) saturate(160%);` +
        `-webkit-backdrop-filter: blur(12px) saturate(160%);` +
        `box-shadow: ${c.elevation};`,
      padding: [8, 12],
      textStyle: { color: c.heading, fontSize: 12 },
    };
  }

  private tooltip(): EChartsOption['tooltip'] {
    return { ...this.tooltipChrome(), trigger: 'axis' };
  }

  /** Solo la parte de la leyenda que depende del tema. */
  private legendChrome(): any {
    const c = this.chrome();

    return {
      inactiveColor: c.muted,
      pageIconColor: c.text,
      pageIconInactiveColor: c.muted,
      pageTextStyle: { color: c.muted },
      textStyle: { color: c.text },
    };
  }

  /** Eje X del original: mes arriba y n de encuestas debajo. */
  private categoryAxis(points: Array<ISeriesPoint>, rotate = 0): any {
    const c = this.chrome();

    return {
      type: 'category',
      data: points.map((p) => p.label),
      axisLine: { show: true, lineStyle: { color: c.axis } },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        color: c.text,
        fontSize: 12,
        interval: 0,
        rotate,
        lineHeight: 15,
        formatter: (label: string, index: number) => {
          const count = points[index]?.count;
          return count !== undefined ? `${label}\n${count}` : label;
        },
      },
    };
  }

  /**
   * Eje Y sin etiquetas con tope 120 (yaxismaxvalue del original). Sobre
   * cristal si conviene una rejilla: una linea punteada muy tenue ayuda a leer
   * los valores sin ensuciar la tarjeta.
   */
  private valueAxis(max: number | 'dataMax' = 120): any {
    const c = this.chrome();

    return {
      type: 'value',
      max,
      min: 0,
      axisLabel: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { color: c.grid, type: 'dashed', width: 1 } },
    };
  }

  /* ------------------------------------------------------------------ */
  /*                     ANCHOR linea NPS historica                     */
  /* ------------------------------------------------------------------ */
  /**
   * Linea con anclas hexagonales y etiqueta de % sobre cada punto.
   * Es la grafica de "NPS MENSUAL" / "NPS Comerciante (historico)".
   */
  npsLine(points: Array<ISeriesPoint>, color: string, rotateLabels = 0): EChartsOption {
    const c = this.chrome();
    const line = this.seriesColor(color);

    return {
      backgroundColor: 'transparent',
      grid: { top: 60, right: 24, bottom: rotateLabels ? 80 : 56, left: 24, containLabel: true },
      tooltip: {
        ...this.tooltip(),
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          const point = points[p.dataIndex];
          const count = point?.count !== undefined ? `${point.count}, ` : '';
          return `${point?.label}<br/>${count}${p.value}%`;
        },
      },
      xAxis: this.categoryAxis(points, rotateLabels),
      yAxis: this.valueAxis(120),
      series: [
        {
          type: 'line',
          data: points.map((p) => p.value),
          symbol: 'diamond',
          symbolSize: 12,
          itemStyle: { color: line, borderColor: c.symbolEdge, borderWidth: 2 },
          lineStyle: {
            color: line,
            shadowBlur: 14,
            shadowColor: this.withAlpha(line, 0.45),
            shadowOffsetY: 5,
            width: 2.4,
          },
          label: {
            show: true,
            color: c.heading,
            fontSize: 14,
            formatter: '{c}%',
            position: 'top',
            distance: 8,
          },
          labelLayout: { hideOverlap: true },
          emphasis: { scale: 1.3 },
        },
      ],
    };
  }

  /**
   * Linea con valores absolutos (Close The Loop: "Historico de detractores").
   */
  countLine(points: Array<ISeriesPoint>, color: string): EChartsOption {
    const c = this.chrome();
    const line = this.seriesColor(color);
    const max = Math.max(...points.map((p) => p.value));

    return {
      backgroundColor: 'transparent',
      grid: { top: 40, right: 24, bottom: 40, left: 24, containLabel: true },
      tooltip: this.tooltip(),
      xAxis: this.categoryAxis(points.map((p) => ({ label: p.label, value: p.value }))),
      yAxis: this.valueAxis(Math.round(max * 1.6)),
      series: [
        {
          type: 'line',
          data: points.map((p) => p.value),
          symbol: 'triangle',
          symbolSize: 10,
          itemStyle: { color: line, borderColor: c.symbolEdge, borderWidth: 2 },
          lineStyle: {
            color: line,
            shadowBlur: 14,
            shadowColor: this.withAlpha(line, 0.4),
            shadowOffsetY: 5,
            width: 2.4,
          },
          label: { show: true, color: c.heading, fontSize: 13, position: 'top', distance: 8 },
          labelLayout: { hideOverlap: true },
        },
      ],
    };
  }

  /* ------------------------------------------------------------------ */
  /*                        ANCHOR area (dashboard)                     */
  /* ------------------------------------------------------------------ */
  npsArea(points: Array<ISeriesPoint>, color = ChartService.COLORS.teal): EChartsOption {
    const c = this.chrome();

    return {
      backgroundColor: 'transparent',
      grid: { top: 70, right: 24, bottom: 50, left: 24, containLabel: true },
      tooltip: this.tooltip(),
      xAxis: { ...this.categoryAxis(points), boundaryGap: false },
      yAxis: this.valueAxis(120),
      series: [
        {
          type: 'line',
          data: points.map((p) => p.value),
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: { color, borderColor: c.symbolEdge, borderWidth: 2 },
          lineStyle: {
            color,
            shadowBlur: 16,
            shadowColor: this.withAlpha(color, 0.5),
            shadowOffsetY: 6,
            width: 2.4,
          },
          // El bloque plano de antes tapaba el cristal: ahora se desvanece.
          areaStyle: { color: this.verticalGradient(color, 0.55, 0), opacity: 1 },
          label: {
            show: true,
            color: c.heading,
            fontSize: 13,
            formatter: '{c}%',
            position: 'top',
            distance: 10,
          },
          labelLayout: { hideOverlap: true },
        },
      ],
    };
  }

  /* ------------------------------------------------------------------ */
  /*                           ANCHOR donut                             */
  /* ------------------------------------------------------------------ */
  /** Donut con el % al centro (ApexCharts del original). */
  donut(value: number, color = ChartService.COLORS.green): EChartsOption {
    const c = this.chrome();

    return {
      backgroundColor: 'transparent',
      tooltip: { show: false },
      series: [
        {
          type: 'pie',
          radius: ['62%', '86%'],
          center: ['50%', '50%'],
          startAngle: 90,
          padAngle: 2,
          silent: true,
          label: {
            show: true,
            position: 'center',
            color: c.heading,
            fontSize: 34,
            fontWeight: 500,
            formatter: `${value}%`,
          },
          labelLine: { show: false },
          // El segundo dato es la pista: su color lo repinta applyTheme.
          data: [
            {
              value,
              itemStyle: {
                borderRadius: 6,
                color,
                shadowBlur: 18,
                shadowColor: this.withAlpha(color, 0.45),
              },
            },
            { value: 100 - value, itemStyle: { borderRadius: 6, color: c.track } },
          ],
        },
      ],
    };
  }

  /* ------------------------------------------------------------------ */
  /*                     ANCHOR barra apilada horizontal                */
  /* ------------------------------------------------------------------ */
  /** "Drivers de recomendacion": una sola barra 100% con N segmentos. */
  stackedBar(items: Array<{ name: string; value: number }>): EChartsOption {
    const c = this.chrome();

    return {
      backgroundColor: 'transparent',
      grid: { top: 30, right: 20, bottom: 10, left: 20, containLabel: false },
      tooltip: { ...this.tooltip(), trigger: 'item', formatter: '{a}: {c}%' },
      legend: {
        ...this.legendChrome(),
        bottom: 0,
        icon: 'roundRect',
        itemHeight: 12,
        itemWidth: 16,
        textStyle: { color: c.text, fontSize: 11 },
        type: 'scroll',
      },
      xAxis: { type: 'value', show: false, max: 100 },
      yAxis: { type: 'category', show: false, data: [''] },
      series: items.map((item, i) => {
        const color = ChartService.STACK_PALETTE[i % ChartService.STACK_PALETTE.length];

        return {
          name: item.name,
          type: 'bar',
          stack: 'total',
          barWidth: 34,
          data: [item.value],
          itemStyle: { borderRadius: 4, color: this.solidGradient(color) },
          label: {
            show: true,
            // La etiqueta va sobre el propio segmento: el color del texto lo
            // decide la luminancia del segmento, no el tema.
            color: this.labelOn(color),
            fontSize: 12,
            fontWeight: 600,
            formatter: '{c}%',
          },
        };
      }),
    };
  }

  /* ------------------------------------------------------------------ */
  /*                        ANCHOR columnas                             */
  /* ------------------------------------------------------------------ */
  /** Columnas simples con etiqueta encima ("Consideracion"). */
  column(
    items: Array<{ name: string; value: number }>,
    color = ChartService.COLORS.teal,
    suffix = '%',
  ): EChartsOption {
    const c = this.chrome();

    return {
      backgroundColor: 'transparent',
      grid: { top: 40, right: 16, bottom: 90, left: 16, containLabel: true },
      tooltip: this.tooltip(),
      xAxis: {
        type: 'category',
        data: items.map((i) => i.name),
        axisLine: { lineStyle: { color: c.axis } },
        axisTick: { show: false },
        axisLabel: {
          color: c.text,
          fontSize: 10,
          interval: 0,
          lineHeight: 12,
          width: 96,
          overflow: 'break',
        },
      },
      yAxis: this.valueAxis('dataMax'),
      series: [
        {
          type: 'bar',
          barWidth: '55%',
          data: items.map((i) => i.value),
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: this.verticalGradient(color, 1, 0.45),
          },
          label: {
            show: true,
            color: c.heading,
            fontSize: 12,
            position: 'top',
            formatter: `{c}${suffix}`,
          },
        },
      ],
    };
  }

  /** Columnas agrupadas con leyenda ("Orientacion por palanca", "Salud del Programa"). */
  groupedColumn(
    categories: Array<string>,
    series: Array<{ name: string; color: string; data: Array<number> }>,
    showLabel = false,
  ): EChartsOption {
    const c = this.chrome();

    return {
      backgroundColor: 'transparent',
      grid: { top: 30, right: 16, bottom: 110, left: 16, containLabel: true },
      tooltip: this.tooltip(),
      legend: {
        ...this.legendChrome(),
        bottom: 0,
        icon: 'roundRect',
        itemHeight: 12,
        itemWidth: 16,
        textStyle: { color: c.text, fontSize: 12 },
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLine: { lineStyle: { color: c.axis } },
        axisTick: { show: false },
        axisLabel: {
          color: c.text,
          fontSize: 10,
          interval: 0,
          lineHeight: 12,
          width: 90,
          overflow: 'break',
        },
      },
      yAxis: this.valueAxis('dataMax'),
      series: series.map((s) => ({
        name: s.name,
        type: 'bar' as const,
        data: s.data,
        itemStyle: {
          borderRadius: [5, 5, 0, 0],
          color: this.verticalGradient(s.color, 1, 0.45),
        },
        label: showLabel
          ? { show: true, color: c.heading, fontSize: 10, position: 'top' as const }
          : { show: false },
      })),
    };
  }

  /* ------------------------------------------------------------------ */
  /*                       ANCHOR mapa de Mexico                        */
  /* ------------------------------------------------------------------ */
  /**
   * Coropleta de estados. `mapName` debe estar registrado antes con
   * echarts.registerMap(mapName, geoJson) — lo hace MapChartComponent.
   */
  mexicoMap(
    mapName: string,
    data: Array<{ name: string; value: number }>,
  ): EChartsOption {
    const c = this.chrome();

    return {
      backgroundColor: 'transparent',
      tooltip: {
        ...this.tooltip(),
        trigger: 'item',
        formatter: (p: any) =>
          p.value !== undefined && !isNaN(p.value)
            ? `${p.name}<br/>NPS: ${p.value}%`
            : `${p.name}<br/>Sin datos`,
      },
      visualMap: {
        type: 'continuous',
        min: 0,
        max: 100,
        left: 10,
        bottom: 10,
        orient: 'horizontal',
        itemWidth: 14,
        itemHeight: 150,
        text: ['100', '0'],
        textStyle: { color: c.text, fontSize: 11 },
        // Semaforo NPS: canal de informacion, invariante entre temas.
        inRange: {
          color: [ChartService.COLORS.red, ChartService.COLORS.amber, ChartService.COLORS.green],
        },
        calculable: false,
      },
      series: [
        {
          type: 'map',
          map: mapName,
          roam: false,
          data,
          nameProperty: 'name',
          itemStyle: {
            areaColor: c.mapEmpty,
            borderColor: c.mapBorder,
            borderWidth: 1,
          },
          emphasis: {
            label: { show: false },
            itemStyle: { areaColor: c.mapHover },
          },
          select: { disabled: true },
          label: { show: false },
        },
      ],
    };
  }

  /* ------------------------------------------------------------------ */
  /*                         ANCHOR applyTheme                          */
  /* ------------------------------------------------------------------ */
  /**
   * Reescribe SOLO el cromo de una opcion ya armada. Las paginas construyen su
   * EChartsOption una vez en ngOnInit; al cambiar de tema, ChartComponent pasa
   * la opcion por aqui y devuelve una copia con los colores del tema nuevo.
   *
   * No se usa structuredClone a proposito: mataria los `formatter` (funciones).
   */
  public applyTheme(option: EChartsOption): EChartsOption {
    const c = this.chrome();
    const next: any = { ...(option as any) };

    if (next.tooltip && next.tooltip.show !== false) {
      next.tooltip = { ...next.tooltip, ...this.tooltipChrome() };
    }

    if (next.legend) {
      next.legend = {
        ...next.legend,
        ...this.legendChrome(),
        textStyle: { ...next.legend.textStyle, color: c.text },
      };
    }

    if (next.visualMap) {
      next.visualMap = {
        ...next.visualMap,
        textStyle: { ...next.visualMap.textStyle, color: c.text },
      };
    }

    next.xAxis = this.themedAxis(next.xAxis, c);
    next.yAxis = this.themedAxis(next.yAxis, c);

    if (Array.isArray(next.series)) {
      next.series = next.series.map((s: any) => this.themedSeries(s, c));
    }

    return next as EChartsOption;
  }

  private themedAxis(axis: any, c: IChartChrome): any {
    if (!axis || Array.isArray(axis)) {
      return axis;
    }

    const next = { ...axis };

    if (next.axisLine?.show !== false) {
      next.axisLine = { ...next.axisLine, lineStyle: { ...next.axisLine?.lineStyle, color: c.axis } };
    }

    if (next.axisLabel && next.axisLabel.show !== false) {
      next.axisLabel = { ...next.axisLabel, color: c.text };
    }

    if (next.splitLine?.show) {
      next.splitLine = { ...next.splitLine, lineStyle: { ...next.splitLine.lineStyle, color: c.grid } };
    }

    return next;
  }

  private themedSeries(series: any, c: IChartChrome): any {
    const next = { ...series };

    switch (next.type) {
      case 'line':
        next.itemStyle = { ...next.itemStyle, borderColor: c.symbolEdge };
        if (next.label?.show) {
          next.label = { ...next.label, color: c.heading };
        }
        break;

      case 'bar':
        // La etiqueta de un segmento apilado va sobre el color de la serie y
        // ya se resolvio con labelOn(): no depende del tema. Solo se retiñe la
        // de la columna suelta, que rotula sobre el cristal.
        if (next.label?.show && !next.stack) {
          next.label = { ...next.label, color: c.heading };
        }
        break;

      case 'pie':
        if (next.label?.show) {
          next.label = { ...next.label, color: c.heading };
        }
        // data[1] es la pista de la dona (ver donut()).
        next.data = (next.data ?? []).map((d: any, i: number) =>
          i === 1 ? { ...d, itemStyle: { ...d.itemStyle, color: c.track } } : d,
        );
        break;

      case 'map':
        next.itemStyle = { ...next.itemStyle, areaColor: c.mapEmpty, borderColor: c.mapBorder };
        next.emphasis = {
          ...next.emphasis,
          itemStyle: { ...next.emphasis?.itemStyle, areaColor: c.mapHover },
        };
        break;
    }

    return next;
  }
}
